import React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Box, Paper, Typography, TextField, MenuItem, InputAdornment, Divider, IconButton } from "@mui/material";
import IbssButton from "../../../../Components/Buttons/Button/IbssButton";
import { DataGrid, GridColDef, GridPaginationModel, GridCallbackDetails } from '@mui/x-data-grid';
import { appContext } from "../../../../AppContext";
import { DateTime } from "luxon";
import { CostCodeSource, CostCodeType, ICostCode } from "../../../../Providers.Api/CostCodes/CostCodeRepository";
import { ComponentHelper } from "../../../../Common/ComponentHelper";
import CostCodesModal from "./CostCodesModal";
import ClearIcon from '@mui/icons-material/Clear';
import IbssDataGrid from "../../../../Components/Data/DataGrid/IbssDataGrid";
import { CostCode } from "./DataModels";
import { PagedResponse } from "../../../../Providers.Api/Models";
import { QueryParams } from "./QueryParams";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { StringHelper } from "../../../../Common/StringHelper";
import IbssPageHeader from "../../../../Components/Headings/PageHeading/IbssPageHeader";

class ListCostCodes extends IbssPage<IProps, IState, QueryParams>
{
    private get apiClient() { return appContext().apiClient; }
    private get appState() { return appContext().state; }
    private get labels() { return appContext().labels; }
    private string = new StringHelper();
    private costCodes: ICostCode[] = [];
    private pageCacheKey = '783da27e-3dbe-4150-9ded-b1ab411e4b1f';

    private columns: GridColDef[] = [
        {
            field: 'typeName',
            headerName: this.labels.HubLabelType,
            width: 215,
            flex: 1,
            sortable: false
        },
        {
            field: 'code',
            headerName: this.labels.HubLabelCode,
            width: 215,
            flex: 1,
            sortable: false
        },
        {
            field: 'name',
            headerName: this.labels.HubLabelName,
            width: 215,
            flex: 1,
            sortable: false
        },
        {
            field: 'lastModified',
            headerName: this.labels.HubLabelLastModified,
            width: 215,
            flex: 1,
            sortable: false
        },
    ]

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.state =
        {
            costCodes: [],
            isLoading: false,
            buildingId: this.appState.buildingId,
            openFilterModal: false,
            costCodeType: CostCodeType.Any,
            costCodeSource: CostCodeSource.Any,
            searchTerm: '',
            skipToken: undefined,
            page: 0,
            pageSize: 25,
        }
    }
    public async queryParamsDidUpdate(firstLoad?: boolean, prevParams?: QueryParams): Promise<void>
    {
        if (this.area === "admin")
        {
            this.pageTitle = this.labels.HubMenuManage
        }
        else
        {
            this.pageTitle = this.labels.HubLabelCostCodes
        }

        const queryParams = this.queryParams;
        if (firstLoad)
        {
            this.clearCache();
        }

        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: this.appState.buildingId }, true);
        }
        else if (!queryParams.type)
        {
            return this.pushQueryParams({ type: CostCodeType.Any }, true);
        }
        else if (!queryParams.source)
        {
            return this.pushQueryParams({ source: CostCodeSource.Any }, true);
        }

        await this.setStateAsync({
            costCodeSource: queryParams.source as CostCodeSource,
            costCodeType: queryParams.type as CostCodeType,
            searchTerm: queryParams?.filter ?? '',
        });

        if (queryParams.building != prevParams?.building ||
            queryParams.type != prevParams?.type ||
            queryParams.source != prevParams?.source ||
            queryParams.filter != prevParams?.filter)
        {
            await this.setStateAsync({
                page: 0,
                skipToken: undefined,
                costCodes: [],
            });
        }

        await this.loadCostCodes();
    }

    private async loadCostCodes(costCodeSource?: CostCodeSource, costCodeType?: CostCodeType, searchTerm?: string): Promise<void>
    {
        try
        {
            this.setState({ isLoading: true });

            const endpoint = {
                name: 'ibssApiClientV2.v2.byNodeid.costcodes.get',
                options: {
                    nodeId: 1,
                    skipToken: this.state.skipToken,
                    top: 1000,
                    select: CostCode,
                    filter: this.createCostCodeQuery(this.state.costCodeSource, this.state.costCodeType, this.state.searchTerm),
                }
            };

            const response = await appContext().inMemoryCache.lazyGetWithQuickExpiry(
                `${this.pageCacheKey}_${JSON.stringify(endpoint)}`,
                () => appContext().ibssApiClientV2.v2.byNodeid.costcodes.get<PagedResponse<ICostCode[]>>(endpoint.options),
            );

            this.costCodes = response.value;

            this.setState({
                costCodes: [
                    ...this.state.costCodes,
                    ...this.mapCostCodes()
                ],
                skipToken: response.skipToken,
            });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async searchTermChanged(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): Promise<void>
    {
        await this.setStateAsync({ searchTerm: e.target.value.trim() });
    }

    private async clearSearch(): Promise<void>
    {
        this.pushQueryParams({ filter: '' });
    }

    private mapCostCodes(): ICostCodeView[]
    {
        const mappedCostCodes = this
            .costCodes
            .map(costCode => (
                {
                    id: parseInt(costCode.Cost_Code_Id),
                    type: costCode.Cost_Code_Type,
                    typeName: this.getCostCodeType(costCode.Cost_Code_Type),
                    code: costCode.Cost_Code,
                    name: costCode.Cost_Code_Description,
                    lastModified: DateTime.fromISO(costCode.EventTime).toLocaleString(DateTime.DATETIME_SHORT),
                    source: costCode.Cost_Code_Source,
                } as ICostCodeView));

        return mappedCostCodes;
    }

    private getCostCodeType(costCode: number | null): string
    {
        if (costCode != null && costCode < 100)
        {
            return CostCodeType.StandardCostCode;
        }
        if (costCode != null && costCode === 100) 
        {
            return CostCodeType.ClientCode;
        }
        if (costCode != null && costCode === 101)
        {
            return CostCodeType.DepartmentCode;
        }
        return '';
    }

    private onLastPage(page: number): boolean
    {
        // detect if user is on the last page of the data in client side.
        return page === this.state.costCodes.length / this.state.pageSize - 1;
    }

    private changePage(page: number): void
    {
        this.setState({ page: page });
        if (this.onLastPage(page))
        {
            this.loadCostCodes();
        }
    }

    private changePageSize(pageSize: number): void
    {
        this.setState({ pageSize: pageSize });
    }

    private async handleFilterCostCodes(source: CostCodeSource, type: CostCodeType): Promise<void>
    {
        this.pushQueryParams({ source: source, type: type })
    }

    private createCostCodeQuery(costCodeSource?: CostCodeSource, costCodeType?: CostCodeType, searchTerm?: string): string
    {
        let query = `Cost_Code_Id ne null and Cost_Code_IsEnabled eq 1`

        if (searchTerm)
        {
            query = query + ` and Cost_Code ge '${searchTerm}' and Cost_Code lt '${this.string.incrementLastCharacter(searchTerm)}'`
        }

        if (costCodeSource != null)
        {
            switch (costCodeSource)
            {
                case CostCodeSource.API:
                    query = query + ` and Cost_Code_Source eq '${CostCodeSource.API}'`
                    break;
                case CostCodeSource.Manual:
                    query = query + ` and Cost_Code_Source eq '${CostCodeSource.Manual}'`
                    break;
                case CostCodeSource.SFTP:
                    query = query + ` and Cost_Code_Source eq '${CostCodeSource.SFTP}'`
                    break;
                // nothing to add to query if costCodeSource is 'Any'
            }
        }

        if (costCodeType != null)
        {
            switch (costCodeType)
            {
                case CostCodeType.ClientCode:
                    query = query + ` and Cost_Code_Type eq 100`;
                    break;

                case CostCodeType.DepartmentCode:
                    query = query + ` and Cost_Code_Type eq 101`;
                    break;

                case CostCodeType.StandardCostCode:
                    query = query + ` and (Cost_Code_Type ge 0 and Cost_Code_Type le 99)`;
                    break;
                // nothing to add to query if CostCodeType is 'Any'
            }
        }
        return query;
    }

    private async clearCache(): Promise<void>
    {
        appContext().inMemoryCache.clear(i => i.startsWith(this.pageCacheKey + '_'));
    }

    public render(): JSX.Element
    {
        return (
            <>
                {/* Filter Modal */}
                <CostCodesModal
                    costCodeSource={this.state.costCodeSource}
                    costCodeType={this.state.costCodeType}
                    changeCostCodeSource={(source) => this.setState({ costCodeSource: source })}
                    changeCostCodeType={(type) => this.setState({ costCodeType: type })}
                    openFilterModal={this.state.openFilterModal}
                    handleFilterModal={(status: boolean) => this.setState({ openFilterModal: status })}
                    handleFilterCostCodes={async (source: CostCodeSource, type: CostCodeType) => { await this.handleFilterCostCodes(source, type); }}
                />
                <Box className="rightPanel-main-content">
                    <Box className="table-panel">
                        <IbssPageHeader
                            title={this.labels.HubLabelCostCodes}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', margin: '20px 0 20px 0' }}>
                            <TextField
                                id="cost-codes-search"
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment position={'end'}>
                                            <IconButton aria-label="clear" size="small" disabled={this.state.searchTerm.length === 0} onClick={() => this.clearSearch()}>
                                                <ClearIcon fontSize={'small'} />
                                            </IconButton>
                                        </InputAdornment>
                                }}
                                size="small"
                                label={this.labels.HubLabelSearch}
                                value={this.state.searchTerm}
                                onChange={e => this.searchTermChanged(e)}
                            />
                            <IbssButton
                                disabled={this.state.searchTerm.length < 5}
                                sx={{ ml: 2, mr: 2 }}
                                variant="contained"
                                onClick={() => { this.pushQueryParams({ filter: this.state.searchTerm }) }} // clicking on search button also filters by costCodeType and costCodeSource because the filtering is progressive.
                            >
                                {this.labels.HubLabelSearch}
                            </IbssButton>
                            <Divider orientation="vertical" flexItem />
                            <IbssButton
                                sx={{ ml: 2, mr: 2 }}
                                variant="contained"
                                onClick={() => this.setState({ openFilterModal: true })}
                            >
                                {this.labels.HubLabelFilter}
                            </IbssButton>
                        </Box>
                        <IbssDataGrid
                            height="calc(100vh - 258px)"
                            aria-label="cost codes table"
                            rows={this.state.costCodes}
                            columns={this.columns}
                            loading={this.state.isLoading}
                            hideFooterSelectedRowCount={true}
                            paginationModel=
                            {{
                                page: this.state.page,
                                pageSize: this.state.pageSize,
                            }}
                            onPaginationModelChange={(model: GridPaginationModel, details: GridCallbackDetails) => 
                            {
                                this.changePage(model.page);
                                this.changePageSize(model.pageSize);
                            }
                            }

                            pageSizeOptions={[25, 50, 100]}
                        />
                    </Box>
                </Box>
            </>
        )
    }
}

export default withRouter(ListCostCodes);

export interface IState
{
    costCodes: ICostCodeView[];
    isLoading: boolean;
    buildingId: number;
    openFilterModal: boolean;
    costCodeType: CostCodeType;
    costCodeSource: CostCodeSource;
    searchTerm: string;
    skipToken: string | undefined;
    page: number;
    pageSize: number;
}

export interface ICostCodeView
{
    id: number;
    type: number | null;
    typeName: string;
    code: string;
    name: string | null;
    lastModified: string;
    source: string;
}

export interface IProps extends RouteComponentProps
{
}
